// "Improve with AI" on a tiptap selection, shared by the prose editor (RichEditor.tsx) and the
// slides text editor (slides/SlidesEditor.tsx): capture the selection → the instruction popover →
// the local agent's async job (passerelle POST /agent/improve, polled) → the host's `apply`. Each
// host supplies its own apply step: how the result is written and recorded in the room's history.
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { Editor } from '@tiptap/react'
import { api, type ImproveResult } from '../../lib/api'
import { getRunnableModel } from '../../lib/agent-prefs'
import { beginAgentTask, useAgentBusy } from '../../lib/agent-busy'
import { useLang } from '../../i18n'

/** A highlighted range awaiting an AI improvement, with a frozen copy of its text. */
export interface PendingSelection {
  from: number
  to: number
  text: string
}

export interface ImproveSelectionOptions {
  editor: Editor | null
  /**
   * Write the agent's result over the captured selection and record it. Throw to drop the result:
   * the message is shown in the popover and nothing is closed. `instruction` is trimmed ('' = none).
   */
  apply: (pending: PendingSelection, improved: string, instruction: string) => void
  /** Extra classes on the popover (a host's "keep the text open" marker). */
  className?: string
}

export interface ImproveSelection {
  /** Capture the editor's selection and open the popover. False: nothing to improve. */
  open: () => boolean
  /** Close the popover (stable across renders). */
  close: () => void
  pending: PendingSelection | null
  /** The agent's job is running. */
  busy: boolean
  /** Another agent task (chat, SVG, an improve elsewhere) holds the agent: opening is disabled. */
  agentBusy: boolean
  /** The popover, portalled to the body (null when closed): render it anywhere. */
  popover: ReactNode
}

export function useImproveSelection({ editor, apply, className }: ImproveSelectionOptions): ImproveSelection {
  const { t } = useLang()
  const [pending, setPending] = useState<PendingSelection | null>(null)
  const [instruction, setInstruction] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const promptRef = useRef<HTMLTextAreaElement>(null)
  // Block opening/running improve while another agent (chat/SVG) is busy; contribute the lock too.
  const agentBusy = useAgentBusy().busy

  useEffect(() => {
    if (pending) promptRef.current?.focus()
  }, [pending])

  function open(): boolean {
    if (!editor || editor.isDestroyed) return false
    const { from, to } = editor.state.selection
    const text = editor.state.doc.textBetween(from, to, '\n')
    if (!text.trim()) return false
    setInstruction('')
    setError(null)
    setPending({ from, to, text })
    return true
  }

  const close = useCallback((): void => {
    setPending(null)
    setBusy(false)
    setError(null)
  }, [])

  /** Cancel / Esc: close, and give the focus back to the text the member was editing. */
  function dismiss(): void {
    close()
    if (editor && !editor.isDestroyed) editor.commands.focus(undefined, { scrollIntoView: false })
  }

  async function run(): Promise<void> {
    if (!editor || !pending) return
    setBusy(true)
    setError(null)
    const release = beginAgentTask()
    try {
      // Async job: submit, then poll the passerelle until the local agent finishes.
      // Improve runs on Claude Code; reuse the member's chosen Claude model if any.
      const { jobId } = await api.agentImprove(
        pending.text,
        instruction.trim(),
        undefined,
        getRunnableModel('Claude Code'),
      )
      let job = await api.agentJob(jobId)
      while (job.status === 'running') {
        await new Promise((r) => setTimeout(r, 3000))
        job = await api.agentJob(jobId)
      }
      if (job.status === 'error') {
        setError(job.error ?? 'The local agent failed.')
        setBusy(false)
        return
      }
      const { improved } = (job.result ?? {}) as ImproveResult
      apply(pending, improved, instruction.trim())
      close()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The local agent failed.')
      setBusy(false)
    } finally {
      release()
    }
  }

  const popover =
    pending &&
    createPortal(
      <div
        className={className ? `ai-pop ${className}` : 'ai-pop'}
        role="dialog"
        aria-modal="true"
        aria-label={t('improve_title')}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !busy) {
            e.stopPropagation()
            dismiss()
          }
        }}
      >
        <div className="ai-pop-head">
          <span className="ai-pop-title">{t('improve_title')}</span>
        </div>
        <blockquote className="ai-pop-excerpt">{pending.text}</blockquote>
        <textarea
          ref={promptRef}
          className="ai-pop-input"
          rows={2}
          aria-label={t('improve_ph')}
          placeholder={t('improve_ph')}
          value={instruction}
          disabled={busy}
          onChange={(e) => setInstruction(e.target.value)}
        />
        {error && <p className="ai-pop-err" role="alert">{error}</p>}
        <div className="ai-pop-acts">
          <button className="mini ghost" onClick={dismiss} disabled={busy}>
            {t('cancel')}
          </button>
          <button className="mini ok" onClick={() => void run()} disabled={busy || agentBusy}>
            {busy ? t('improving') : t('improve_run')}
          </button>
        </div>
      </div>,
      document.body,
    )

  return { open, close, pending, busy, agentBusy, popover }
}
